/**
 * AI Product Mapping — match-policy helpers.
 *
 * A row's scoring data is the same shape whether the matcher committed or not: a best
 * candidate and a runner-up. Matched rows expose the chosen result as the top (alternatives
 * are the runner-ups); no-match rows expose their alternatives best-first (the server keeps
 * the top candidates on a nomatch so the reviewer can still pick one). `topCandidate`
 * normalizes both so the cell, the "use top suggestion" action, and the reclassification
 * policy all read one shape.
 *
 * `applyPolicy` re-buckets a row from its raw scores under a user-tunable policy, entirely
 * client-side (no re-run): the matcher always sends the candidate confidences, so lowering
 * the confidence bar or the ambiguity gate can promote rows the server rejected. The default
 * mirrors the matcher (min confidence 0.60, ambiguity spread 0.10), so the default view
 * matches what the server committed — only the sliders change anything.
 */

import { REMATCH_REASON, ROW_STATUS } from './constants';
import type { GridRow, MatchPolicy, RowStatus, TopCandidate } from './types';

export const HI_CONFIDENCE = 0.85;
// minConfidence: match vs no-match floor. matchedAt: "Matched" vs "Low confidence" cut.
// ambiguitySpread: flag ambiguous when top-2 are within this. All reviewer-tunable.
export const DEFAULT_MATCH_POLICY: MatchPolicy = {
  minConfidence: 0.6,
  matchedAt: HI_CONFIDENCE,
  ambiguitySpread: 0.1,
};

// Reasons where the engine refused on PRODUCT TYPE, not on score: the catalog carries no
// products of the row's type at all, or the top candidate is a different product family.
// Their candidates are cross-category look-alikes, so lowering the confidence sliders must
// never promote them — the scores were never the problem.
export const TYPE_GATED_REASONS: ReadonlySet<string> = new Set(['type_not_in_catalog', 'type_mismatch']);

/** A row the policy helpers can read — the live grid passes full `GridRow`s.
 *  `candidates` is `readonly` so a frozen/`as const` row (e.g. in tests) is accepted. */
interface ScorableRow {
  matchedSku: string | null;
  matchedTitle?: string | null;
  matchedProductId?: string | null;
  confidence?: number;
  candidates?: readonly GridRow['candidates'][number][];
  status?: RowStatus;
  reason?: string;
  confirmed?: boolean;
  edited?: boolean;
  promoted?: boolean;
  markedNoMatch?: boolean;
}

/**
 * The confidence of the best-ranked candidate arguing for a DIFFERENT product — what the
 * ambiguity flag should measure. Sibling variants of the chosen product never argue with
 * the product decision (the engine's tie-break owns the variant pick), so they are
 * skipped; a candidate whose product is unknown (older stored rows / frames without ids)
 * conservatively counts as a rival, preserving the old behavior there. Null = no rival.
 */
export function rivalConfidence(
  candidates: readonly GridRow['candidates'][number][] | undefined,
  ownProductId: string | null | undefined,
): number | null {
  for (const candidate of candidates || []) {
    if (ownProductId && candidate.productId && candidate.productId === ownProductId) continue;
    return candidate.conf ?? candidate.confidence ?? 0;
  }
  return null;
}

export function topCandidate(row: ScorableRow | null | undefined): TopCandidate | null {
  if (!row) return null;
  // Matched / manually-edited rows: the chosen SKU is the top; the runner-up is the best
  // RIVAL-product alternative (a size sibling can't contest the product decision).
  if (row.matchedSku) {
    return {
      sku: row.matchedSku,
      title: row.matchedTitle ?? null,
      confidence: row.confidence ?? 0,
      runnerUp: rivalConfidence(row.candidates, row.matchedProductId) ?? 0,
    };
  }
  // Unmatched rows: alternatives are best-first, so the first is the best guess; its
  // runner-up is likewise the best candidate from a different product.
  const [first, ...rest] = row.candidates || [];
  if (!first) return null;
  return {
    sku: first.sku,
    title: first.title ?? null,
    confidence: first.conf ?? 0,
    runnerUp: rivalConfidence(rest, first.productId) ?? 0,
  };
}

// A row that carries at least one candidate the reviewer could accept — true for matches
// and for no-match rows the matcher rejected on confidence/ambiguity but still ranked
// candidates for. False only for genuine dead ends (no_candidates) and rows still pending.
export const hasCandidates = (row: ScorableRow | null | undefined): boolean => topCandidate(row) != null;

/** A no-match somebody actually DECIDED: the reviewer's mark / a group rejection
 *  (`markedNoMatch`) or the type gate's refusal (the catalog carries nothing of this
 *  kind — settled by construction, the row copy already says "no review needed"). The
 *  mirror of a confirmed match; everything else in the no-match bucket is still an open
 *  question and belongs to Needs review. */
export const decidedNoMatch = (row: ScorableRow): boolean =>
  Boolean(row.markedNoMatch || (row.reason && TYPE_GATED_REASONS.has(row.reason)));

/** An unresolved row the reviewer still has to act on: a low/ambiguous/no-match/error bucket
 *  with no decision recorded (confirm, edit, mark-as-gap, or a settled refusal). One
 *  predicate shared by the `needs_review` tally and the grid's Needs-review filter so the
 *  chip count always matches the rows the filter shows. */
export const needsReview = (row: ScorableRow): boolean =>
  (row.status === ROW_STATUS.LOW ||
    row.status === ROW_STATUS.MULTI ||
    row.status === ROW_STATUS.NOMATCH ||
    row.status === ROW_STATUS.ERROR) &&
  !row.confirmed &&
  !row.edited &&
  !decidedNoMatch(row);

/** The rows a bulk confirm can actually touch: processed rows that are either already
 *  confirmed (undo candidates) or have something to confirm (a committed match or a
 *  ranked best guess). In-flight rows, errors, and dead ends never participate. */
export function bulkConfirmTargets(rows: readonly GridRow[], idxs: number[]): GridRow[] {
  const targets: GridRow[] = [];
  for (const idx of idxs) {
    const row = rows[idx - 1];
    if (!row) continue;
    if (row.status === ROW_STATUS.PENDING || row.status === ROW_STATUS.PROCESSING || row.status === ROW_STATUS.ERROR) continue;
    if (!row.confirmed && !row.matchedSku && !topCandidate(row)) continue;
    targets.push(row);
  }
  return targets;
}

/** Bulk confirm mirrors the per-row toggle only when EVERY touchable row is already
 *  confirmed — then the click is an undo of the previous confirm. Any unconfirmed row in
 *  the selection makes it a set operation instead, so a sweep can't un-confirm strays. */
export const isBulkConfirmUndo = (targets: readonly GridRow[]): boolean =>
  targets.length > 0 && targets.every((row) => row.confirmed);

/** The rows a bulk mark-as-no-match can touch: anything already processed (the per-row
 *  mark button allows error rows too — only in-flight rows are out). */
export function bulkMarkNoMatchTargets(rows: readonly GridRow[], idxs: number[]): GridRow[] {
  const targets: GridRow[] = [];
  for (const idx of idxs) {
    const row = rows[idx - 1];
    if (!row) continue;
    if (row.status === ROW_STATUS.PENDING || row.status === ROW_STATUS.PROCESSING) continue;
    targets.push(row);
  }
  return targets;
}

/** Same undo rule as bulk confirm: only an ALL-marked selection un-marks (the undo of the
 *  previous bulk mark); any unmarked row keeps it a set operation. */
export const isBulkMarkNoMatchUndo = (targets: readonly GridRow[]): boolean =>
  targets.length > 0 && targets.every((row) => row.markedNoMatch);

// Bucket a row's best candidate under the policy: below the confidence floor → no match;
// above it but too close to the runner-up → ambiguous; at/above the matched bar → matched,
// otherwise low confidence. Merged with defaults so a partial policy still resolves every bar.
export function classify(top: TopCandidate | null, policy: Partial<MatchPolicy> = DEFAULT_MATCH_POLICY): RowStatus {
  if (!top) return ROW_STATUS.NOMATCH;
  const { minConfidence, matchedAt, ambiguitySpread } = { ...DEFAULT_MATCH_POLICY, ...policy };
  if (top.confidence < minConfidence) return ROW_STATUS.NOMATCH;
  if (top.confidence - top.runnerUp <= ambiguitySpread) return ROW_STATUS.MULTI;
  return top.confidence >= matchedAt ? ROW_STATUS.HI : ROW_STATUS.LOW;
}

// Project a row through the policy. User decisions (confirmed/edited), errors, and in-flight
// rows are authoritative and pass through untouched. A no-match row the policy now accepts is
// "promoted" — its best candidate is surfaced as the match so the cell + counts treat it as
// one; accepting it (use-top / picker) is still what persists the choice.
export function applyPolicy<T extends ScorableRow>(row: T, policy: Partial<MatchPolicy> = DEFAULT_MATCH_POLICY): T {
  // A user-marked gap renders as a no-match wherever the policy view feeds (grid, view counts);
  // the raw row keeps its match data so the editor can still surface the AI suggestions.
  if (row.markedNoMatch) {
    return { ...row, status: ROW_STATUS.NOMATCH, matchedSku: null, matchedTitle: null, candidates: [], confidence: 0 } as T;
  }
  if (
    row.confirmed ||
    row.edited ||
    row.status === ROW_STATUS.ERROR ||
    row.status === ROW_STATUS.PENDING ||
    row.status === ROW_STATUS.PROCESSING
  ) {
    return row;
  }
  // Type-gated refusals are score-independent: the candidates are cross-category
  // look-alikes, so no slider setting may resurrect them as matches.
  if (row.reason && TYPE_GATED_REASONS.has(row.reason)) {
    return row;
  }
  // A constrained re-match is a reviewer decision, not a score verdict: its runner-ups
  // are sibling variants of the product the reviewer chose, so the sliders must neither
  // demote it to ambiguous nor re-bucket it by confidence.
  if (row.reason === REMATCH_REASON) {
    return row;
  }
  const top = topCandidate(row);
  const status = classify(top, policy);
  if (status === row.status) return row;
  if (status !== ROW_STATUS.NOMATCH && !row.matchedSku && top) {
    return {
      ...row,
      status,
      promoted: true,
      matchedSku: top.sku,
      matchedTitle: top.title,
      confidence: top.confidence,
    } as T;
  }
  return { ...row, status } as T;
}
